import { useEffect, useState } from "react";
import { Button, useBackLayer } from "@hybrid-iptv/ui";
import {
  MAX_PROFILES,
  PROFILE_AVATARS,
  profileSwitchNeedsPin,
  useProfiles,
  type Profile,
} from "../settings/profileStore.js";
import { useParental } from "../settings/parentalStore.js";

const PIN_LENGTH = 4;

/** Same reasoning as the parental pad: real buttons, never a text input, so a
 * TV remote can drive it without summoning a soft keyboard. */
function PinPad({ value, onChange }: { value: string; onChange: (u: (prev: string) => string) => void }) {
  const press = (d: string) => onChange((prev) => (prev.length < PIN_LENGTH ? prev + d : prev));
  return (
    <div className="pinpad">
      <div className="pinpad__display" aria-label={`${value.length} of ${PIN_LENGTH} digits entered`}>
        {Array.from({ length: PIN_LENGTH }, (_, i) => (
          <span key={i} className={`pinpad__dot ${i < value.length ? "pinpad__dot--filled" : ""}`} />
        ))}
      </div>
      <div className="pinpad__grid">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          // The pad opens with the ring on 1: the screen it replaced took the
          // focused button with it (onn box, 2026-10-06).
          <button key={d} type="button" className="pinpad__key" onClick={() => press(d)} aria-label={`Digit ${d}`} autoFocus={d === "1"}>
            {d}
          </button>
        ))}
        <button type="button" className="pinpad__key" onClick={() => onChange(() => "")} aria-label="Clear">
          Clear
        </button>
        <button type="button" className="pinpad__key" onClick={() => press("0")} aria-label="Digit 0">
          0
        </button>
        <button
          type="button"
          className="pinpad__key"
          onClick={() => onChange((prev) => prev.slice(0, -1))}
          aria-label="Delete"
        >
          Back
        </button>
      </div>
    </div>
  );
}

export function ProfilesPage() {
  const { profiles, activeProfileId, addProfile, updateProfile, removeProfile, setActiveProfile } = useProfiles();
  const pinHash = useParental((s) => s.pinHash);
  const verifyPin = useParental((s) => s.verifyPin);

  const [draft, setDraft] = useState<{ name: string; avatar: string; isKids: boolean } | null>(null);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<string | null>(null);
  const [entry, setEntry] = useState("");

  const active = profiles.find((p) => p.id === activeProfileId);

  // The form and the PIN pad replace the list, so the button that opened them
  // is gone while they are up: OK on "+ Add Profile" left nothing ringed
  // (onn box, 2026-10-06). They open with the ring inside them, Back closes them
  // like any layer, and closing puts the ring back on what opened them.
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const cancelDraft = () => { setDraft(null); setEditing(null); setNote(null); };
  const cancelPin = () => { setPinFor(null); setEntry(""); setNote(null); };
  useBackLayer(draft !== null && pinFor === null, cancelDraft);
  useBackLayer(pinFor !== null, cancelPin);
  useEffect(() => {
    if (draft || pinFor || !returnTo) return;
    const back = document.querySelector<HTMLElement>(`[data-return="${returnTo}"]`) ?? document.querySelector<HTMLElement>("[data-return]");
    back?.focus();
    setReturnTo(null);
  }, [draft, pinFor, returnTo]);

  const attemptSwitch = (id: string) => {
    if (id === activeProfileId) return;
    if (profileSwitchNeedsPin(active, Boolean(pinHash))) {
      setReturnTo(`profile:${id}`);
      setPinFor(id);
      setEntry("");
      setNote("Enter the PIN to leave the Kids profile.");
      return;
    }
    setActiveProfile(id);
    setNote(null);
  };

  const confirmPin = async () => {
    if (!pinFor) return;
    if (!(await verifyPin(entry))) {
      setEntry("");
      setNote("Incorrect PIN.");
      return;
    }
    if (pinFor.startsWith("remove:")) {
      const id = pinFor.slice("remove:".length);
      const error = removeProfile(id);
      setConfirmRemoveId(null);
      setPinFor(null);
      setEntry("");
      setNote(error);
      if (!error) setReturnTo(`profile:${useProfiles.getState().activeProfileId}`);
      return;
    }
    setActiveProfile(pinFor);
    setPinFor(null);
    setEntry("");
    setNote(null);
  };

  const handleRemove = (id: string) => {
    if (confirmRemoveId !== id) {
      setConfirmRemoveId(id);
      return;
    }
    const profile = profiles.find((candidate) => candidate.id === id);
    const needsParentalPin = Boolean(pinHash) && (profile?.isKids === true || profiles.length === 1);
    if (needsParentalPin) {
      setReturnTo("remove");
      setPinFor(`remove:${id}`);
      setEntry("");
      setNote("Enter the parental PIN to remove this protected profile.");
      return;
    }
    const error = removeProfile(id);
    setConfirmRemoveId(null);
    setNote(error);
    // The ring onto the profile now active. Left on Remove Active, two more
    // presses removed THAT profile as well (onn box, 2026-10-06).
    if (!error) setTimeout(() => document.querySelector<HTMLElement>(`[data-return="profile:${useProfiles.getState().activeProfileId}"]`)?.focus(), 0);
  };

  const saveDraft = () => {
    if (!draft) return;
    if (editing) {
      updateProfile(editing.id, { name: draft.name.trim(), avatar: draft.avatar, isKids: draft.isKids });
      setEditing(null);
      setDraft(null);
      setNote(null);
      return;
    }
    const error = addProfile(draft);
    if (error) {
      setNote(error);
      // Back up to the name, where the message shows above it: pressed from
      // Create at the bottom, the message appeared off screen.
      document.querySelector<HTMLElement>(".settings-input")?.focus();
      return;
    }
    setDraft(null);
    setNote(null);
  };

  if (pinFor) {
    return (
      <div className="page page--source">
        <div className="settings-card">
          <div className="settings-header">
            <h1 className="settings-title">Enter PIN</h1>
            <p className="settings-subtitle">
              {pinFor.startsWith("remove:")
                ? "Removing this protected profile needs the parental PIN."
                : "Leaving a Kids profile needs the parental PIN."}
            </p>
          </div>
          {note && <p className="settings-save-note">{note}</p>}
          <PinPad value={entry} onChange={setEntry} />
          <div className="settings-actions">
            <Button variant="primary" onClick={() => void confirmPin()}>
              Confirm
            </Button>
            <Button variant="ghost" onClick={cancelPin}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (draft) {
    return (
      <div className="page page--source">
        <div className="settings-card">
          <div className="settings-header">
            <h1 className="settings-title">{editing ? `Edit ${editing.name}` : "New Profile"}</h1>
          </div>
          {note && <p className="settings-save-note">{note}</p>}

          <label className="settings-field">
            <span className="settings-label">Name</span>
            <input
              type="text"
              className="settings-input"
              autoFocus
              placeholder="e.g. Sam"
              enterKeyHint="next"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              onKeyDown={(e) => {
                // Enter on the keyboard while typing: on to the avatars, which
                // also puts the keyboard away. It stayed up with the ring in the
                // box, and only Back got out (onn box, 2026-10-06). The OK that
                // OPENS the keyboard never gets here (keyboardOnDemand.ts takes it).
                if (e.key !== "Enter" || e.defaultPrevented) return;
                e.preventDefault();
                (document.querySelector<HTMLElement>(".profile-avatar--on") ?? document.querySelector<HTMLElement>(".profile-avatar"))?.focus();
              }}
            />
          </label>

          <span className="settings-label">Avatar</span>
          <div className="profile-avatars">
            {PROFILE_AVATARS.map((a) => (
              <button
                key={a}
                type="button"
                className={`profile-avatar ${draft.avatar === a ? "profile-avatar--on" : ""}`}
                onClick={() => setDraft({ ...draft, avatar: a })}
                aria-label={`Avatar ${a}`}
                aria-pressed={draft.avatar === a}
              >
                {a}
              </button>
            ))}
          </div>

          {/* One full-width row, in line with the rest: the small checkbox sat
              off to the side, Down from the avatars went past it to Create, and
              Right from it jumped up into the avatars (onn box, 2026-10-06). */}
          <button
            type="button"
            className="profile-kids"
            role="switch"
            aria-checked={draft.isKids}
            onClick={() => setDraft({ ...draft, isKids: !draft.isKids })}
          >
            <span>Kids profile</span>
            <span className={`profile-kids__state ${draft.isKids ? "profile-kids__state--on" : ""}`}>{draft.isKids ? "On" : "Off"}</span>
          </button>
          <p className="settings-save-note">
            A Kids profile always hides adult categories, even when no PIN is set. Leaving it
            asks for the parental PIN when one exists. Social sign-in will be adult-only and
            linked separately to each eligible profile.
          </p>

          <div className="settings-actions">
            <Button variant="primary" onClick={saveDraft}>
              {editing ? "Save" : "Create Profile"}
            </Button>
            <Button variant="ghost" onClick={cancelDraft}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--source">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Who's watching?</h1>
          <p className="settings-subtitle">
            Each profile keeps its own favourites and continue-watching. Social sign-in will be
            separate for each profile and will not make viewing history an online account. Up to {MAX_PROFILES}.
          </p>
        </div>

        {note && <p className="settings-save-note">{note}</p>}

        <div className="provider-list">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`provider-item ${p.id === activeProfileId ? "provider-item--active" : ""}`}
              onClick={() => attemptSwitch(p.id)}
              data-return={`profile:${p.id}`}
              aria-pressed={p.id === activeProfileId}
            >
              <span className="provider-item__name">
                <span className="profile-avatar profile-avatar--inline">{p.avatar}</span> {p.name}
                {p.isKids ? " · Kids" : ""}
              </span>
              <span className="provider-item__status">{p.id === activeProfileId ? "● Active" : ""}</span>
            </button>
          ))}
        </div>

        <div className="settings-actions">
          <Button
            variant="primary"
            data-return="add"
            onClick={() => { setReturnTo("add"); setEditing(null); setDraft({ name: "", avatar: PROFILE_AVATARS[1], isKids: false }); setNote(null); }}
          >
            + Add Profile
          </Button>
          {active && (
            <Button
              variant="default"
              data-return="edit"
              onClick={() => { setReturnTo("edit"); setEditing(active); setDraft({ name: active.name, avatar: active.avatar, isKids: active.isKids }); setNote(null); }}
            >
              Edit Active
            </Button>
          )}
          {active && profiles.length > 1 && (
            <Button
              variant={confirmRemoveId === active.id ? "danger" : "ghost"}
              data-return="remove"
              onClick={() => handleRemove(active.id)}
            >
              {confirmRemoveId === active.id ? "Confirm Remove" : "Remove Active"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
